import { DEFAULT_REFERRAL_USES_PER_USER } from 'common/Constants';
import { ClientEvents, ReferralShareLocation, ShareType } from 'common/events/ClientEvents';
import { formatCurrency } from 'common/FormatUtilities';
import { getReferralValues } from 'common/ReferralValueUtilities';
import { formatTotalValue } from 'common/ReferTextUtilities';
import { useCallback, useState } from 'react';
import { useFeatureFlag } from 'src/feature-flags/consumers';
import { useCurrentUser } from 'src/shared/hooks/useCurrentUserHook';
import { useShare } from 'src/shared/hooks/useShare';
import { useTracker } from 'src/shared/hooks/useTracker';
import { getReferralUrl } from 'src/shared/utils/PromoUtilities';

const SHARE_COPY = 'Share link';
const COPIED_COPY = 'Link copied';

export const useReferralShare = (analyticsLocation: ReferralShareLocation) => {
  const [currentUser] = useCurrentUser();
  const tracker = useTracker();
  const newReferralValuesEnabled = useFeatureFlag('newReferralValuesEnabled');
  const { giveValue } = getReferralValues(newReferralValuesEnabled);

  const giveText = formatCurrency(giveValue, true);
  const totalGiveText = formatTotalValue({ giveValue, maxUsesPerUser: DEFAULT_REFERRAL_USES_PER_USER });

  const referralShareMessage =
    giveText && totalGiveText
      ? `Get ${totalGiveText} off! You'll receive ${giveText} off your first ${DEFAULT_REFERRAL_USES_PER_USER} orders using my referral link.`
      : '';

  const code = currentUser?.referralCode?.code ?? '';
  const referralLink = getReferralUrl({ code });
  const [copyText, setCopyText] = useState<React.ReactNode>(SHARE_COPY);

  const onShare = useCallback(
    (type: ShareType) => {
      tracker.track(ClientEvents.REFERRAL_CODE_SHARED, {
        code,
        type,
        location: analyticsLocation,
      });
      if (type !== ShareType.NATIVE) {
        setCopyText(COPIED_COPY);
        setTimeout(() => {
          setCopyText(SHARE_COPY);
        }, 2000);
      }
    },
    [analyticsLocation, code, tracker]
  );

  const { share } = useShare({
    url: referralLink,
    shareMessage: referralShareMessage,
    onShare,
    onError: () => null,
  });

  return {
    share,
    copyText,
    referralLink,
    referralShareMessage,
    code,
    currentUser,
  };
};
